import React from 'react'
import type { ToteSelection, AllUpSelection, Acceptor, Race } from '@mobi/betslip/types'
import { addAcceptorNameToSingleSelection } from '@core/Areas/Quickbet/Components/BetSelectionInfo/Components/BetInfoHeader/helpers'
import { NoveltyListStyled, NoveltyPlaceStyled, NoveltyStarterStyled } from '../../../Common.styles'
import { PlaceTextStyled } from '../../../PlaceText'

interface MultiRaceSelectionProps {
  selection: ToteSelection | AllUpSelection
  races: Race[]
  acceptors: Acceptor[]
  displayPill: boolean
}

export const MultiRaceSelection = ({
  selection,
  races,
  acceptors,
  displayPill,
}: MultiRaceSelectionProps) => {
  const parsed =
    selection.betType === 'All Up'
      ? parseAllUpSelections(selection as AllUpSelection, races, acceptors)
      : parseMultiLegSelectionString(selection as ToteSelection, races, acceptors)
  return (
    <NoveltyListStyled data-tid-multi-selection=''>
      {Object.keys(parsed).map(key => [
        <NoveltyPlaceStyled key={`__${key}`}>
          <PlaceTextStyled text={key} displayPill={displayPill} />
        </NoveltyPlaceStyled>,
        <NoveltyStarterStyled key={key}>{parsed[key]}</NoveltyStarterStyled>,
      ])}
    </NoveltyListStyled>
  )
}

// =============
// Local Helpers
// =============

function parseAllUpSelections(
  { details }: AllUpSelection,
  races: Race[],
  allAcceptors: Acceptor[]
): MultiRaceSelections {
  if (details.length !== races.length) {
    // don't return anything because it's probably wrong
    return {}
  }

  const parsed: MultiRaceSelections = {}
  details.forEach((selectionDetails, index) => {
    const race = races[index]
    const key = `Race ${race.raceNumber}`
    parsed[key] = `(${getPoolTypeName(
      selectionDetails.poolType
    )}) ${addAcceptorNameToSingleSelection(
      selectionDetails.betSelections,
      getAcceptorsForRace(race, allAcceptors)
    )}`
  })

  return parsed
}

function parseMultiLegSelectionString(
  { selectionString }: ToteSelection,
  races: Race[],
  allAcceptors: Acceptor[]
): MultiRaceSelections {
  const selectionStringParts = selectionString.split('/')

  if (selectionStringParts.length !== races.length) {
    // don't return anything because it's probably wrong
    return {}
  }

  const parsed: MultiRaceSelections = {}
  selectionStringParts.forEach((selection, index) => {
    const race = races[index]
    const key = `Race ${race.raceNumber}`
    parsed[key] = addAcceptorNameToSingleSelection(
      selection,
      getAcceptorsForRace(race, allAcceptors)
    )
  })

  return parsed
}

function getPoolTypeName(poolType: AllUpSelection['details'][0]['poolType']): string {
  switch (poolType) {
    case 'W': {
      return 'Win'
    }
    case 'P': {
      return 'Place'
    }
    case 'EW': {
      return 'Eachway'
    }
    case 'Q': {
      return 'Quinella'
    }
    default: {
      return ''
    }
  }
}
function getAcceptorsForRace(race: Race, allAcceptors: Acceptor[]): Acceptor[] {
  return allAcceptors.filter(acceptor => race.acceptorKeys.includes(acceptor.key))
}

// =====
// Types
// =====

type MultiRaceSelections = Record<string, string>
